import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { arcPath, bandPositions, barDomain, barPath, CHART_SLOTS, chartSlotColor, describeCategories, describeComposition, describeGroups, describeSeries, describeStacks, donutArcs, extentOf, foldToOther, groupBars, labelledIndices, measuredPoints, niceTicks, padExtent, plural, polylinePath, scaleLinear, sharePercent, stackBars, } from "./charts-scale.js";
/**
 * The numbers behind a chart, for a reader who cannot see it.
 *
 * Exported because every chart in the package owes one, and a second
 * implementation is a second chance to forget the gap row.
 */
export function ChartDataTable({ caption, points, pointHeader = "Point", valueHeader, format = (value) => String(value), gapText = "Not measured", }) {
    return (_jsxs("table", { className: "esm-visually-hidden", children: [_jsx("caption", { children: caption }), _jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { scope: "col", children: pointHeader }), _jsx("th", { scope: "col", children: valueHeader })] }) }), _jsx("tbody", { children: points.map((point, index) => (
                // The label is not guaranteed unique — two models can share a name
                // across providers — so the index is part of the key.
                _jsxs("tr", { children: [_jsx("th", { scope: "row", children: point.label }), _jsx("td", { children: point.value === null || !Number.isFinite(point.value) ? gapText : format(point.value) })] }, `${index}-${point.label}`))) })] }));
}
/**
 * A bare line: no axes, no labels, no ticks. It answers "which way has this
 * been going", and it is deliberately incapable of answering "how much" —
 * that is what the StatTile beside it, and the hidden table, are for.
 */
export function Sparkline({ name, points, format = (value) => String(value), valueHeader, pointHeader = "Day", width = 240, height = 40, className, }) {
    const measured = measuredPoints(points);
    if (measured.length < 2) {
        // Two different facts, and a chart that rendered the same thing for both
        // would be hiding the more interesting one.
        return (_jsx("p", { className: className ? `esm-chart__empty ${className}` : "esm-chart__empty", children: measured.length === 0
                ? `${name}: nothing measured in this window.`
                : `${name}: only one point measured — a line needs two.` }));
    }
    // Half the stroke width, so the line at the top or bottom of the range is
    // not clipped in half by the viewBox edge.
    const inset = 1.5;
    const domain = padExtent(extentOf(points.map((point) => point.value)) ?? { min: 0, max: 1 });
    const x = scaleLinear({ min: 0, max: points.length - 1 }, inset, width - inset);
    const y = scaleLinear(domain, height - inset, inset);
    const d = polylinePath(points.map((point, index) => point.value === null || !Number.isFinite(point.value) ? null : { x: x(index), y: y(point.value) }));
    const summary = describeSeries(name, points, format);
    return (_jsxs("div", { className: className ? `esm-sparkline ${className}` : "esm-sparkline", children: [_jsx("svg", { className: "esm-sparkline__svg", viewBox: `0 0 ${width} ${height}`, width: "100%", height: height, 
                // "none" because a sparkline's job is the shape over the width it is
                // given; letterboxing it to keep the aspect ratio would leave gaps in
                // a table cell. vectorEffect below keeps the stroke from stretching
                // with it, which is the one thing "none" would otherwise ruin.
                preserveAspectRatio: "none", role: "img", "aria-label": summary, focusable: "false", children: _jsx("path", { className: "esm-sparkline__line", d: d, fill: "none", strokeLinecap: "round", strokeLinejoin: "round", vectorEffect: "non-scaling-stroke" }) }), _jsx(ChartDataTable, { caption: summary, points: points, pointHeader: pointHeader, valueHeader: valueHeader ?? name, format: format })] }));
}
const DELTA_ARROW = { up: "▲", down: "▼", flat: "■" };
const DELTA_WORD = { up: "up", down: "down", flat: "unchanged" };
/** One headline figure, its label, and optionally how it has moved. */
export function StatTile({ label, value, unavailable = "Not available", delta, sparkline, hint }) {
    return (_jsxs("div", { className: "esm-stat", children: [_jsx("div", { className: "esm-stat__label", children: label }), value === null ? (_jsxs("div", { className: "esm-stat__value esm-stat__value--absent", title: unavailable, children: [_jsx("span", { "aria-hidden": "true", children: "\u2014" }), _jsx("span", { className: "esm-visually-hidden", children: unavailable })] })) : (_jsx("div", { className: "esm-stat__value", children: value })), delta ? _jsx(StatDeltaLine, { delta: delta }) : null, hint ? _jsx("p", { className: "esm-stat__hint", children: hint }) : null, sparkline ? _jsx("div", { className: "esm-stat__spark", children: sparkline }) : null] }));
}
function StatDeltaLine({ delta }) {
    if (delta.text === null) {
        return (_jsxs("p", { className: "esm-stat__delta esm-stat__delta--none", children: ["No comparable baseline", delta.caption ? _jsxs("span", { className: "esm-stat__delta-caption", children: [" ", delta.caption] }) : null] }));
    }
    const sentiment = delta.sentiment ?? "neutral";
    return (_jsxs("p", { className: `esm-stat__delta esm-stat__delta--${sentiment}`, children: [_jsxs("span", { "aria-hidden": "true", children: [DELTA_ARROW[delta.direction], " "] }), _jsx("span", { children: delta.text }), " ", _jsx("span", { className: "esm-visually-hidden", children: DELTA_WORD[delta.direction] }), delta.caption ? _jsxs("span", { className: "esm-stat__delta-caption", children: [" ", delta.caption] }) : null] }));
}
/** The identity key for a chart. Always present where hue means something. */
export function ChartLegend({ items, className }) {
    return (_jsx("ul", { className: className ? `esm-chart-legend ${className}` : "esm-chart-legend", children: items.map((item, index) => (_jsxs("li", { className: "esm-chart-legend__item", children: [_jsx("span", { className: "esm-chart-legend__swatch", style: { background: item.color }, "aria-hidden": "true" }), _jsx("span", { className: "esm-chart-legend__label", children: item.label }), item.value === undefined ? null : _jsx("span", { className: "esm-chart-legend__value", children: item.value }), item.note ? _jsx("span", { className: "esm-chart-legend__note", children: item.note }) : null] }, `${index}-${item.label}`))) }));
}
/** ChartDataTable's multi-series sibling: the numbers behind a stacked bar. */
export function ChartMatrixTable({ caption, rowHeader, rows, columns }) {
    return (_jsxs("table", { className: "esm-visually-hidden", children: [_jsx("caption", { children: caption }), _jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { scope: "col", children: rowHeader }), columns.map((column, index) => (_jsx("th", { scope: "col", children: column.header }, `${index}-${column.header}`)))] }) }), _jsx("tbody", { children: rows.map((row, rowIndex) => (_jsxs("tr", { children: [_jsx("th", { scope: "row", children: row }), columns.map((column, columnIndex) => (_jsx("td", { children: column.cells[rowIndex] }, `${columnIndex}-${column.header}`)))] }, `${rowIndex}-${row}`))) })] }));
}
/** A stated reason something is missing from the picture. Visible, because a
 *  sighted reader cannot see an undrawn segment either. */
function ChartNote({ children }) {
    return _jsx("p", { className: "esm-chart__note", children: children });
}
function EmptyChart({ className, children }) {
    return _jsx("p", { className: className ? `esm-chart__empty ${className}` : "esm-chart__empty", children: children });
}
/* The viewBox is in units that equal CSS pixels at the design width below, so
 * the mark specs — a 24-unit bar cap, a 4-unit rounded data end, a 2-unit
 * surface gap — mean what they say on a full-width card. Wider or narrower,
 * the browser scales them uniformly; nothing here measures the DOM. */
const CHART_WIDTH = 720;
/**
 * Horizontal bars: which of these is biggest.
 *
 * Horizontal rather than vertical because the labels are names — model
 * aliases, application names, email addresses — and a name reads along a row
 * without being rotated, truncated or thinned out. Columns are for time, and
 * that is what StackedBarChart draws.
 *
 * The figures sit in a fixed column on the right rather than at each bar's
 * tip. A tip label has nowhere to go when the bar is nearly full width, and
 * nothing here can measure text to find out; a reserved gutter always fits and
 * lines the numbers up as a column while it is at it.
 */
export function BarChart({ name, points, format = (value) => String(value), colorBy = "series", maxCategories = CHART_SLOTS, labelColumn = 168, valueGutter = 96, valueHeader, pointHeader = "Category", className, }) {
    const folded = colorBy === "category" ? foldToOther(points, maxCategories) : { points: points.slice(), folded: 0, droppedUnmeasured: 0 };
    const rows = folded.points;
    if (rows.length === 0) {
        return _jsx(EmptyChart, { className: className, children: `${name}: nothing to show.` });
    }
    if (measuredPoints(rows).length === 0) {
        return _jsx(EmptyChart, { className: className, children: `${name}: nothing measured in this window.` });
    }
    const top = 8;
    const rowHeight = 28;
    const axisBand = 26;
    const plotBottom = top + rows.length * rowHeight;
    const plotLeft = labelColumn + 12;
    const plotRight = CHART_WIDTH - valueGutter;
    const height = plotBottom + axisBand;
    const domain = barDomain(rows.map((row) => row.value));
    const x = scaleLinear(domain, plotLeft, plotRight);
    const ticks = niceTicks(domain, 4);
    const bands = bandPositions(rows.length, top, plotBottom, { gap: 6, maxThickness: 24 });
    const zero = x(0);
    const summary = describeCategories(name, rows, format);
    return (_jsxs("div", { className: className ? `esm-chart ${className}` : "esm-chart", children: [_jsxs("svg", { className: "esm-chart__svg", viewBox: `0 0 ${CHART_WIDTH} ${height}`, width: "100%", 
                // "meet", unlike the sparkline's "none": this drawing contains text
                // and rounded corners, and a non-uniform stretch would shear both.
                preserveAspectRatio: "xMidYMid meet", role: "img", "aria-label": summary, focusable: "false", children: [ticks.map((tick) => (_jsxs("g", { children: [_jsx("line", { className: "esm-chart__grid", x1: x(tick), y1: top, x2: x(tick), y2: plotBottom }), _jsx("text", { className: "esm-chart__tick", x: x(tick), y: plotBottom + 16, textAnchor: "middle", children: format(tick) })] }, `tick-${tick}`))), _jsx("line", { className: "esm-chart__axis", x1: zero, y1: top, x2: zero, y2: plotBottom }), rows.map((row, index) => {
                        const band = bands[index];
                        if (band === undefined) {
                            return null;
                        }
                        const middle = band.center + 4;
                        const color = colorBy === "category" ? chartSlotColor(index) : chartSlotColor(0);
                        const measured = row.value !== null && Number.isFinite(row.value);
                        const value = measured ? row.value : 0;
                        const end = x(value);
                        const negative = value < 0;
                        return (_jsxs("g", { children: [_jsx("text", { className: "esm-chart__label", x: labelColumn, y: middle, textAnchor: "end", children: row.label }), measured ? (_jsx("path", { d: barPath(negative ? end : zero, band.start, Math.abs(end - zero), band.size, 4, negative ? "left" : "right"), fill: color })) : null, _jsx("text", { className: "esm-chart__value", x: plotRight + 8, y: middle, textAnchor: "start", children: measured ? format(value) : "Not measured" })] }, `bar-${index}-${row.label}`));
                    })] }), colorBy === "category" ? (_jsx(ChartLegend, { items: rows.map((row, index) => ({
                    label: row.label,
                    color: chartSlotColor(index),
                    value: row.value === null || !Number.isFinite(row.value) ? undefined : format(row.value),
                    note: row.value === null || !Number.isFinite(row.value) ? "not measured" : undefined,
                })) })) : null, folded.folded > 0 ? (_jsx(ChartNote, { children: `${plural(folded.folded, "smaller category is", "smaller categories are")} grouped as Other.` })) : null, folded.droppedUnmeasured > 0 ? (_jsx(ChartNote, { children: `${plural(folded.droppedUnmeasured, "category was", "categories were")} not measured, and is not in Other — an unmeasured reading added to a sum becomes a zero.` })) : null, _jsx(ChartDataTable, { caption: summary, points: points, pointHeader: pointHeader, valueHeader: valueHeader ?? name, format: format })] }));
}
/**
 * Columns made of parts: how a total was composed, bar by bar.
 *
 * Segments are separated by a 2-unit gap of surface rather than by a stroke —
 * a stroke around a mark is ink that is not data, and it darkens every reading
 * it surrounds. A segment left with no height after the gap is not drawn at
 * all, and the count of those is stated below the chart instead of being left
 * to look like a rendering fault.
 */
export function StackedBarChart({ name, labels, series, format = (value) => String(value), maxLabels = 7, pointHeader = "Day", className, }) {
    if (labels.length === 0 || series.length === 0) {
        return _jsx(EmptyChart, { className: className, children: `${name}: nothing to show.` });
    }
    const stacks = stackBars(labels, series);
    const summary = describeStacks(name, series.map((entry) => entry.name), stacks, format);
    // Two different windows, and the same blank chart for both would hide the
    // more interesting one: a window nobody measured is a reporting failure, a
    // window of real zeros is a quiet week.
    if (stacks.every((stack) => stack.segments.every((segment) => segment === null))) {
        return _jsx(EmptyChart, { className: className, children: `${name}: nothing measured in this window.` });
    }
    if (!stacks.some((stack) => stack.total > 0)) {
        return _jsx(EmptyChart, { className: className, children: `${name}: every bar is zero in this window.` });
    }
    const top = 12;
    const plotBottom = 192;
    const axisBand = 26;
    const valueAxis = 56;
    const plotLeft = valueAxis + 4;
    const plotRight = CHART_WIDTH - 8;
    const height = plotBottom + axisBand;
    const segmentGap = 2;
    const domain = barDomain(stacks.map((stack) => stack.total));
    const y = scaleLinear(domain, plotBottom, top);
    const ticks = niceTicks(domain, 4);
    const bands = bandPositions(stacks.length, plotLeft, plotRight, { gap: 4, maxThickness: 24 });
    const written = new Set(labelledIndices(stacks.length, maxLabels));
    // Worked out before the render so the note below the chart can state it.
    // The alternative is a segment that is in the legend, in the table and
    // nowhere on screen, with nothing to say why.
    // A stack with a hole in it has an unknown baseline for everything above
    // the hole: the segments that ARE measured get drawn from zero, so the bar
    // is honest about its own total and silent about what is missing under it.
    // Naming those bars is what stops the short column reading as a quiet day.
    const partial = stacks.filter((stack) => stack.unmeasured + stack.undrawable > 0).map((stack) => stack.label);
    let tooThin = 0;
    const drawn = stacks.map((stack, stackIndex) => {
        const band = bands[stackIndex];
        const lowest = stack.segments.findIndex((segment) => segment !== null);
        let highest = -1;
        stack.segments.forEach((segment, index) => {
            if (segment !== null) {
                highest = index;
            }
        });
        return stack.segments.map((segment, index) => {
            if (segment === null || band === undefined) {
                return null;
            }
            const bottom = y(segment.start) - (index === lowest ? 0 : segmentGap);
            const path = barPath(band.start, y(segment.end), band.size, bottom - y(segment.end), 4, index === highest ? "top" : "none");
            if (path === "" && segment.value > 0) {
                tooThin += 1;
            }
            return path === "" ? null : { path, color: series[index]?.color ?? chartSlotColor(index) };
        });
    });
    return (_jsxs("div", { className: className ? `esm-chart ${className}` : "esm-chart", children: [_jsxs("svg", { className: "esm-chart__svg", viewBox: `0 0 ${CHART_WIDTH} ${height}`, width: "100%", preserveAspectRatio: "xMidYMid meet", role: "img", "aria-label": summary, focusable: "false", children: [ticks.map((tick) => (_jsxs("g", { children: [_jsx("line", { className: "esm-chart__grid", x1: plotLeft, y1: y(tick), x2: plotRight, y2: y(tick) }), _jsx("text", { className: "esm-chart__tick", x: valueAxis - 8, y: y(tick) + 4, textAnchor: "end", children: format(tick) })] }, `tick-${tick}`))), _jsx("line", { className: "esm-chart__axis", x1: plotLeft, y1: plotBottom, x2: plotRight, y2: plotBottom }), drawn.map((segments, stackIndex) => (_jsx("g", { children: segments.map((segment, index) => segment === null ? null : (_jsx("path", { d: segment.path, fill: segment.color }, `segment-${index}`))) }, `stack-${stackIndex}-${labels[stackIndex] ?? ""}`))), stacks.map((stack, index) => {
                        const band = bands[index];
                        if (band === undefined || !written.has(index)) {
                            return null;
                        }
                        return (_jsx("text", { className: "esm-chart__tick", x: band.center, y: plotBottom + 16, textAnchor: "middle", children: stack.label }, `axis-${index}-${stack.label}`));
                    })] }), _jsx(ChartLegend, { items: series.map((entry, index) => ({
                    label: entry.name,
                    color: entry.color ?? chartSlotColor(index),
                    value: format(stacks.reduce((sum, stack) => {
                        const segment = stack.segments[index];
                        return sum + (segment ? segment.value : 0);
                    }, 0)),
                })) }), tooThin > 0 ? (_jsx(ChartNote, { children: `${plural(tooThin, "segment is", "segments are")} too small to draw at this size; the figures are in the table.` })) : null, partial.length > 0 ? (_jsx(ChartNote, { children: `${plural(partial.length, "bar is", "bars are")} missing a segment and is drawn short: ` +
                    `${partial.slice(0, 5).join(", ")}${partial.length > 5 ? `, and ${partial.length - 5} more` : ""}.` })) : null, _jsx(ChartMatrixTable, { caption: summary, rowHeader: pointHeader, rows: labels, columns: [
                    ...series.map((entry, index) => ({
                        header: entry.name,
                        cells: stacks.map((stack) => {
                            const segment = stack.segments[index];
                            return segment === null || segment === undefined ? "Not measured" : format(segment.value);
                        }),
                    })),
                    { header: "Total", cells: stacks.map((stack) => format(stack.total)) },
                ] })] }));
}
/**
 * Columns side by side: three readings a day, compared against one axis.
 *
 * The distinction from StackedBarChart is not stylistic. A stack claims its
 * parts COMPOSE its total, which is a claim about the data and not about the
 * picture, and it can only be drawn for parts that are all non-negative.
 * Revenue, cost and margin satisfy neither condition: margin is revenue minus
 * cost rather than a part of it, and a written-off day makes it negative —
 * the one day an operator opened the page to find. So the bars sit beside each
 * other on a signed axis, and a negative reading hangs below the zero line
 * where it can be read as the loss it is.
 *
 * Groups are separated by surface, and so are the bars inside them, by less.
 * The two gaps are what say which bars belong together without a box drawn
 * round each group — ink that would not be data.
 */
export function GroupedBarChart({ name, labels, series, format = (value) => String(value), maxLabels = 7, valueAxis = 88, pointHeader = "Day", className, }) {
    if (labels.length === 0 || series.length === 0) {
        return _jsx(EmptyChart, { className: className, children: `${name}: nothing to show.` });
    }
    const groups = groupBars(labels, series);
    const seriesNames = series.map((entry) => entry.name);
    const summary = describeGroups(name, seriesNames, groups, format);
    const readings = groups.flatMap((group) => group.bars.map((bar) => (bar === null ? null : bar.value)));
    // The same three-way split the stacked chart makes, for the same reason: a
    // window nobody measured is a reporting failure and a window of real zeros
    // is a quiet week, and one blank rectangle for both hides the first.
    if (readings.every((value) => value === null)) {
        return _jsx(EmptyChart, { className: className, children: `${name}: nothing measured in this window.` });
    }
    if (readings.every((value) => value === null || value === 0)) {
        return _jsx(EmptyChart, { className: className, children: `${name}: every bar is zero in this window.` });
    }
    const top = 12;
    const plotBottom = 192;
    const axisBand = 26;
    const plotLeft = valueAxis + 4;
    const plotRight = CHART_WIDTH - 8;
    const height = plotBottom + axisBand;
    const domain = barDomain(readings);
    const y = scaleLinear(domain, plotBottom, top);
    const ticks = niceTicks(domain, 4);
    const slots = bandPositions(groups.length, plotLeft, plotRight, { gap: 8, maxThickness: 96 });
    const written = new Set(labelledIndices(groups.length, maxLabels));
    // Where a reading of zero sits. With an all-positive domain this IS the
    // bottom of the plot; with a negative margin in the window it rises, and
    // every bar is measured from it rather than from the frame.
    const zero = y(0);
    let tooThin = 0;
    const drawn = groups.map((group, groupIndex) => {
        const slot = slots[groupIndex];
        if (slot === undefined) {
            return [];
        }
        const bands = bandPositions(series.length, slot.start, slot.start + slot.size, {
            gap: 2,
            maxThickness: 22,
        });
        return group.bars.map((bar, index) => {
            const band = bands[index];
            if (bar === null || band === undefined) {
                return null;
            }
            const end = y(bar.value);
            const negative = bar.value < 0;
            const path = barPath(band.start, negative ? zero : end, band.size, Math.abs(end - zero), 3, negative ? "bottom" : "top");
            if (path === "" && bar.value !== 0) {
                tooThin += 1;
            }
            return path === "" ? null : { path, color: series[index]?.color ?? chartSlotColor(index) };
        });
    });
    const missing = groups.filter((group) => group.unmeasured > 0).map((group) => group.label);
    return (_jsxs("div", { className: className ? `esm-chart ${className}` : "esm-chart", children: [_jsxs("svg", { className: "esm-chart__svg", viewBox: `0 0 ${CHART_WIDTH} ${height}`, width: "100%", preserveAspectRatio: "xMidYMid meet", role: "img", "aria-label": summary, focusable: "false", children: [ticks.map((tick) => (_jsxs("g", { children: [_jsx("line", { className: "esm-chart__grid", x1: plotLeft, y1: y(tick), x2: plotRight, y2: y(tick) }), _jsx("text", { className: "esm-chart__tick", x: valueAxis - 8, y: y(tick) + 4, textAnchor: "end", children: format(tick) })] }, `tick-${tick}`))), _jsx("line", { className: "esm-chart__axis", x1: plotLeft, y1: zero, x2: plotRight, y2: zero }), drawn.map((bars, groupIndex) => (_jsx("g", { children: bars.map((bar, index) => bar === null ? null : _jsx("path", { d: bar.path, fill: bar.color }, `bar-${index}`)) }, `group-${groupIndex}-${labels[groupIndex] ?? ""}`))), groups.map((group, index) => {
                        const slot = slots[index];
                        if (slot === undefined || !written.has(index)) {
                            return null;
                        }
                        return (_jsx("text", { className: "esm-chart__tick", x: slot.center, y: plotBottom + 16, textAnchor: "middle", children: group.label }, `axis-${index}-${group.label}`));
                    })] }), _jsx(ChartLegend, { items: series.map((entry, index) => {
                    let sum = 0;
                    let measured = 0;
                    for (const group of groups) {
                        const bar = group.bars[index];
                        if (bar) {
                            sum += bar.value;
                            measured += 1;
                        }
                    }
                    return {
                        label: entry.name,
                        color: entry.color ?? chartSlotColor(index),
                        value: measured === 0 ? undefined : format(sum),
                        note: measured === 0 ? "not measured" : undefined,
                    };
                }) }), tooThin > 0 ? (_jsx(ChartNote, { children: `${plural(tooThin, "bar is", "bars are")} too small to draw at this size; the figures are in the table.` })) : null, missing.length > 0 ? (_jsx(ChartNote, { children: `${plural(missing.length, "group is", "groups are")} missing a bar: ` +
                    `${missing.slice(0, 5).join(", ")}${missing.length > 5 ? `, and ${missing.length - 5} more` : ""}.` })) : null, _jsx(ChartMatrixTable, { caption: summary, rowHeader: pointHeader, rows: labels, columns: series.map((entry, index) => ({
                    header: entry.name,
                    cells: groups.map((group) => {
                        const bar = group.bars[index];
                        return bar === null || bar === undefined ? "Not measured" : format(bar.value);
                    }),
                })) })] }));
}
/**
 * A ring: how one whole divides.
 *
 * Deliberately limited. A ring answers "roughly what proportion" at a glance
 * and nothing else — it cannot rank two slices that are close, which is why
 * the legend carries every figure and its percentage in words beside it. If
 * the question is "which is bigger", that is a BarChart.
 *
 * A composition that sums to zero draws NO ring. The alternative is one slice
 * covering the whole circle, which is a picture of a busy day; the component
 * states the zero instead.
 */
export function DonutChart({ name, points, format = (value) => String(value), maxParts = CHART_SLOTS, centerLabel, centerCaption, valueHeader, pointHeader = "Part", className, }) {
    const folded = foldToOther(points, maxParts);
    const rows = folded.points;
    const size = 200;
    const centre = size / 2;
    const outer = 92;
    const inner = 58;
    // The 2-unit surface gap, converted to the angle that subtends it halfway
    // through the ring's thickness.
    const gapAngle = 2 / ((outer + inner) / 2);
    const composition = donutArcs(rows, { gapAngle });
    const summary = describeComposition(name, rows, format, { gapAngle });
    if (rows.length === 0 || composition.measured === 0 || composition.total === 0) {
        return _jsx(EmptyChart, { className: className, children: summary });
    }
    return (_jsxs("div", { className: className ? `esm-chart esm-chart--donut ${className}` : "esm-chart esm-chart--donut", children: [_jsxs("svg", { className: "esm-chart__ring", viewBox: `0 0 ${size} ${size}`, width: "100%", preserveAspectRatio: "xMidYMid meet", role: "img", "aria-label": summary, focusable: "false", children: [composition.arcs.map((arc, index) => arc === null || !arc.drawable ? null : (_jsx("path", { d: arcPath(centre, centre, outer, inner, arc.startAngle, arc.endAngle), fill: chartSlotColor(index) }, `arc-${index}-${arc.label}`))), _jsx("text", { className: "esm-chart__ring-figure", x: centre, y: centre, textAnchor: "middle", children: centerLabel ?? format(composition.total) }), centerCaption ? (_jsx("text", { className: "esm-chart__ring-caption", x: centre, y: centre + 18, textAnchor: "middle", children: centerCaption })) : null] }), _jsx(ChartLegend, { items: rows.map((row, index) => {
                    const arc = composition.arcs[index];
                    if (arc === null || arc === undefined) {
                        const negative = row.value !== null && Number.isFinite(row.value) && row.value < 0;
                        return {
                            label: row.label,
                            color: chartSlotColor(index),
                            value: negative ? format(row.value) : undefined,
                            note: negative ? "negative — no share of a whole" : "not measured",
                        };
                    }
                    const share = sharePercent(arc.value, composition.total);
                    return {
                        label: row.label,
                        color: chartSlotColor(index),
                        value: `${format(arc.value)} · ${share}%`,
                        note: arc.drawable ? undefined : "too thin to draw",
                    };
                }) }), folded.folded > 0 ? (_jsx(ChartNote, { children: `${plural(folded.folded, "smaller part is", "smaller parts are")} grouped as Other.` })) : null, folded.droppedUnmeasured > 0 ? (_jsx(ChartNote, { children: `${plural(folded.droppedUnmeasured, "part was", "parts were")} not measured, and is not in Other.` })) : null, _jsx(ChartDataTable, { caption: summary, points: points, pointHeader: pointHeader, valueHeader: valueHeader ?? name, format: format })] }));
}
//# sourceMappingURL=charts.js.map